import React, { useCallback, useState } from "react";
import dayjs from "dayjs";

import { API_get_labor_recap_report } from "../../../../../constants";
import DateSelector from "../../../plan/daily_top_bar_components/DateSelector";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import ReportEmailToggle from "../ReportEmailToggle";
import {
  ReportScopeFilters,
  scopedStoreOptions,
  useCompanyReportScope,
  useScopedReportFetch,
} from "./_shared/companyReportScope";
import {
  lastCompletedBusinessDay,
  lastCompletedBusinessWeekStart,
} from "./_shared/reportDates";

// Daily / Weekly Labor Recap: the recap email's content in the library. The
// backend builds it with the same code as the email (one company at a time,
// every store the user manages there), so this file only lays it out.

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function snapToWeekStart(date, weekStartDayIndex) {
  const d = startOfDay(date);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

const money = (n, digits = 2) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;

const signedMoney = (n) => {
  const rounded = Math.round(Number(n || 0));
  return `${rounded > 0 ? "+" : rounded < 0 ? "-" : ""}${money(Math.abs(rounded), 0)}`;
};

const signedHours = (n) => {
  const rounded = Math.round(Number(n || 0));
  return `${rounded > 0 ? "+" : ""}${rounded} hrs`;
};

// One "▲ 12% (+$340) vs forecast" line. upIsGood flips the colors: more
// sales is good, more labor hours than earned/scheduled is not.
function ChangeLine({ ratio, detail, label, upIsGood }) {
  const pct = Math.round(Number(ratio || 0) * 100);
  const tone =
    pct === 0
      ? "text-slate-500"
      : (pct > 0) === upIsGood
      ? "text-emerald-600"
      : "text-red-600";
  const arrow = pct > 0 ? "▲ " : pct < 0 ? "▼ " : "";
  return (
    <p className={`text-xs ${tone}`}>
      {arrow}
      {Math.abs(pct)}% ({detail}) {label}
    </p>
  );
}

function Metric({ label, value, valueClassName = "text-slate-900", children }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`text-lg font-bold mt-0.5 mb-1 ${valueClassName}`}>{value}</p>
      {children}
    </div>
  );
}

function SalesAndLaborMetrics({ row }) {
  return (
    <>
      <Metric label="Sales" value={money(row.actual_sales)}>
        <ChangeLine
          ratio={row.sales_to_initial_forecast}
          detail={signedMoney(row.sales_diff_vs_initial_forecast)}
          label="vs forecast"
          upIsGood
        />
        {row.has_adjustment && (
          <ChangeLine
            ratio={row.sales_to_forecast}
            detail={signedMoney(row.sales_diff_vs_forecast)}
            label="vs adjusted"
            upIsGood
          />
        )}
      </Metric>
      <Metric
        label="Labor"
        value={`${Number(row.actual_hours || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} hrs`}
      >
        <ChangeLine
          ratio={row.actual_to_earned}
          detail={signedHours(row.hours_diff)}
          label="vs earned"
          upIsGood={false}
        />
        <ChangeLine
          ratio={row.actual_to_scheduled}
          detail={signedHours(row.actual_vs_scheduled_diff)}
          label="vs scheduled"
          upIsGood={false}
        />
      </Metric>
      <Metric label="SPLH" value={money(row.splh)} />
      <Metric
        label="Labor %"
        value={row.labor_percent > 0 ? `${Number(row.labor_percent).toFixed(2)}%` : "—"}
      />
    </>
  );
}

function SummaryCard({ summary }) {
  if (!summary) return null;
  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-4 mb-3">
      <p className="text-sm font-semibold text-violet-900 mb-3">{summary.label}</p>
      <div className="grid grid-cols-4 gap-4 sm:grid-cols-2">
        <SalesAndLaborMetrics row={summary} />
      </div>
    </div>
  );
}

const TIER_TONES = {
  1: { pill: "bg-emerald-50 text-emerald-700", bar: "bg-emerald-600" },
  2: { pill: "bg-yellow-50 text-yellow-700", bar: "bg-yellow-600" },
  3: { pill: "bg-red-50 text-red-600", bar: "bg-red-600" },
};
const NEUTRAL_TONE = { pill: "bg-slate-100 text-slate-500", bar: "bg-slate-400" };
const tierTone = (tier) => TIER_TONES[tier] || NEUTRAL_TONE;

const INSIGHTS = [
  { label: "Forecast Accuracy", text: "adjustment_made_text", tier: "adjustment_made_tier" },
  { label: "Schedule Adherence", text: "followed_recommendations_text", tier: "followed_recommendations_tier" },
  { label: "Schedule Execution", text: "last_minute_change_text", tier: "last_minute_change_tier" },
];

function StoreCard({ store }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 mb-3 break-inside-avoid">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-base font-semibold text-slate-900">{store.store_name}</p>
          {store.result && (
            <span
              className={`inline-block mt-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                tierTone(store.result_tier).pill
              }`}
            >
              {store.result}
            </span>
          )}
        </div>
        {store.calculated_grade && (
          <div
            className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center text-xl font-bold text-white"
            style={{ backgroundColor: store.calculated_grade_color || "#64748b" }}
            title="Labor grade"
          >
            {store.calculated_grade}
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-4 sm:grid-cols-2">
        <SalesAndLaborMetrics row={store} />
        {store.auto_clockout_count > 0 && (
          <Metric
            label="Auto Clock-outs"
            value={store.auto_clockout_count}
            valueClassName="text-red-600"
          />
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-1">
        {INSIGHTS.map((insight) =>
          store[insight.text] ? (
            <div key={insight.label} className="flex gap-2.5 rounded-md bg-slate-50 px-3 py-2">
              {/* Inset accent bar, quote style, colored by the insight's tier. */}
              <span
                className={`w-[3px] shrink-0 rounded-full ${tierTone(store[insight.tier]).bar}`}
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {insight.label}
                </p>
                <p className="text-xs text-slate-700 mt-0.5">{store[insight.text]}</p>
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

function CompanySection({ company, summaryLabel }) {
  return (
    <section className="mb-8">
      <h4 className="text-base font-semibold text-slate-900 mb-3">{company.company_name}</h4>
      {company.show_company_summary && (
        <SummaryCard
          summary={
            summaryLabel ? { ...company.summary, label: summaryLabel } : company.summary
          }
        />
      )}
      {company.districts?.length
        ? company.districts.map((district) => (
            <div key={district.district_name} className="mb-5">
              <p className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
                {district.district_name}
              </p>
              {company.show_district_summary && <SummaryCard summary={district.summary} />}
              {district.stores.map((store) => (
                <StoreCard key={store.store_id} store={store} />
              ))}
            </div>
          ))
        : (company.stores || []).map((store) => (
            <StoreCard key={store.store_id} store={store} />
          ))}
    </section>
  );
}

function LaborRecapReport({
  report,
  onBack,
  homeStore,
  homeCompany,
  storeNames,
  storeOptions: memberships,
  userId,
  period,
}) {
  const isWeekly = period === "weekly";
  const scope = useCompanyReportScope({
    report,
    homeStore,
    homeCompany,
    storeNames,
    memberships,
  });
  const { companyId, anchorStoreId, weekStartDayIndex, storeFilter } = scope;

  // Until the user picks a period, daily shows the selected company's last
  // completed business day and weekly its last full business week (the
  // periods the emails cover). Both are derived in render, so a company
  // switch moves the default and requests it once. A picked week is
  // re-snapped to the selected company's week start.
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedWeekStart, setPickedWeekStart] = useState(null);
  const date = pickedDate ?? lastCompletedBusinessDay(storeNames, anchorStoreId);
  const weekStart = pickedWeekStart
    ? snapToWeekStart(addDays(pickedWeekStart, 3), weekStartDayIndex)
    : lastCompletedBusinessWeekStart(storeNames, anchorStoreId, weekStartDayIndex);

  const requestParams =
    homeStore && companyId
      ? {
          period,
          company_id: companyId,
          store_id: homeStore,
          date: dayjs(isWeekly ? weekStart : date).format("YYYY-MM-DD"),
          ...(storeFilter.storeIds ? { store_ids: storeFilter.storeIds } : {}),
        }
      : null;
  const { payload, isLoading, error, retry } = useScopedReportFetch(
    API_get_labor_recap_report,
    requestParams,
    {
      onData: scope.acceptPayloadCompany,
      notFoundMessage: "You don't manage any stores with labor recaps.",
      errorMessage: "Couldn't load the labor recap. Try again.",
    }
  );

  const incrementDay = useCallback((current, days) => {
    setPickedDate(addDays(startOfDay(current), days));
  }, []);
  const incrementWeek = useCallback((current, direction) => {
    setPickedWeekStart(addDays(current, direction * 7));
  }, []);
  const setWeekStartDate = useCallback(
    (newDate) => setPickedWeekStart(snapToWeekStart(newDate, weekStartDayIndex)),
    [weekStartDayIndex]
  );

  const storeOptions = scopedStoreOptions(payload, companyId);
  const closedStoreNames = (payload?.closed_store_ids || [])
    .map((id) => storeOptions.find((s) => s.id === id)?.store_name)
    .filter(Boolean);

  const filters = (
    <div className="flex flex-wrap items-center gap-3">
      {isWeekly ? (
        <WeekSelector
          start_date={weekStart}
          end_date={addDays(weekStart, 6)}
          increment_week={incrementWeek}
          set_week_start_date={setWeekStartDate}
          businessWeekStartDayIndex={weekStartDayIndex}
          // "Today" is the week holding the selected company's current
          // business day.
          today={addDays(lastCompletedBusinessDay(storeNames, anchorStoreId), 1)}
        />
      ) : (
        <DateSelector
          date={date}
          increment_day={incrementDay}
          // "Today" is the selected company's current business day.
          today={addDays(lastCompletedBusinessDay(storeNames, anchorStoreId), 1)}
        />
      )}
      <ReportScopeFilters idPrefix="labor-recap" payload={payload} scope={scope} />
    </div>
  );

  const periodLabel = isWeekly
    ? `${dayjs(weekStart).format("ddd, MMM D")} – ${dayjs(addDays(weekStart, 6)).format(
        "ddd, MMM D, YYYY"
      )}`
    : dayjs(date).format("dddd, MMMM D, YYYY");
  const companies = payload?.companies || [];

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      actions={<ReportEmailToggle reportId={report.id} userId={userId} />}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={retry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : payload ? (
        <div>
          <p className="text-sm text-slate-600 mb-4">Labor recap for {periodLabel}</p>
          {closedStoreNames.length > 0 && (
            <p className="text-xs text-slate-500 mb-4">
              Closed this day: {closedStoreNames.join(", ")}
            </p>
          )}
          {companies.length ? (
            companies.map((company) => (
              <CompanySection
                key={company.company_name}
                company={company}
                summaryLabel={
                  // Narrowed to one district, the "company" totals are that
                  // district's totals.
                  storeFilter.key.startsWith("district:")
                    ? `${storeFilter.key.slice("district:".length)} Totals`
                    : undefined
                }
              />
            ))
          ) : (
            <div className="text-sm text-gray-500 py-12 text-center border border-slate-200 rounded-lg">
              No labor recap data for this {isWeekly ? "week" : "day"}.
            </div>
          )}
        </div>
      ) : null}
    </PrebuiltReportShell>
  );
}

export function DailyLaborRecapReport(props) {
  return <LaborRecapReport {...props} period="daily" />;
}

export function WeeklyLaborRecapReport(props) {
  return <LaborRecapReport {...props} period="weekly" />;
}
